import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { DateTime } from 'luxon';
import { useForm, Controller } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Button,
  Chip,
  Unstable_Grid2 as Grid,
  IconButton,
  InputAdornment,
  Link,
  Stack,
  Typography,
} from '@mui/material';
import type { AutocompleteRenderGetTagProps } from '@mui/material/Autocomplete';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useApolloClient, useMutation } from '@apollo/client';

import {
  CREATE_FIND_MUTATION,
  GET_CLOUDFLARE_DIRECT_LINK_QUERY,
  GetAmcrFindPreviewsQueryName,
  GetMyFindQueryName,
  reconcileFindsInCache,
  UPDATE_FIND_MUTATION,
  updateFindTagAssignmentInCache,
} from '../../gql';
import { handleUploadImage } from '../../io';
import { CustomCircularProgress } from '../CustomCiruclarProgress';
import type { AmcrPasFile } from '../AmcrPasDialog';
import { AmcrPasControl } from './AmcrPasControl';
import { DeleteFindControl } from './DeleteFindControl';
import { ImageList } from './ImageList';
import { RequiredLabel } from '../RequiredLabel';
import { PreviewMapContainer } from './PreviewMapContainer';
import { FIND_SETTINGS_FORM_ID } from './constants';
import { FormattedMessage, useIntl } from 'react-intl';
import { useVocabulary } from '../../i18n';
import { formatDateTime } from '../../helpers';
import {
  DateTimeField,
  FieldLabel,
  FormField,
  Paper,
  ImageDropzone,
  MaterialAutocomplete,
  SurfaceAutocomplete,
  SurfaceTextField,
  Switch,
} from '../design-system';

interface FindGroupOption {
  id: string;
  name: string;
  shared?: boolean;
  level?: number | null;
}

interface FindSiteOption {
  id: string | null;
  name: string;
}

interface FindFormProps {
  id: string;
  find?: {
    createdAt?: string;
    name?: string;
    evId?: string;
    localization?: string;
    latitude?: string | number;
    longitude?: string | number;
    foundAt?: string;
    description?: string;
    type?: string;
    era?: string;
    material?: string;
    note?: string;
    visibility: 'public' | 'private';
    siteId?: string | null;
    amcrId?: string;
    amcrStatus?: string | null;
    amcrData?: { images: string[]; files?: AmcrPasFile[] | null };
    shared?: boolean;
    tags: string[];
    groupIds?: string[] | null;
    image?: string;
    images?: string[];
  };
  materials: string[];
  eras: {
    id: string;
    label: string;
    parentId: string;
    parentLabel: string;
  }[];
  types: { id: string; category: string }[];
  sites: { id: string; name: string; amcrProjectIds?: string[] | null }[];
  groups: FindGroupOption[];
  tags: string[];
  site?: string;
  initialGroupId?: string;
  isClone?: boolean;
  onSaveDisabledChange?: (disabled: boolean) => void;
}

const containerStyles = { width: '100%' };
const NO_PAS_FILES: AmcrPasFile[] = [];
const sectionStyles = {
  flex: 1,
  minWidth: 300,
};
const situationSectionStyles = {
  ...sectionStyles,
  display: 'flex',
  flexDirection: 'column',
};

const inputStyles = { width: '100%', padding: 0 };
const amcrIdInputStyles = { width: { xs: '100%', sm: '50%' }, padding: 0 };
const endAdornmentStyles = { marginRight: '12px' };
const gpsInputStyles = { flex: 1 };
const dangerStyles = {
  fontWeight: 650,
  fontSize: 14,
  color: 'error.main',
};
const imageSectionContentStyles = { mt: 1 };
const submitButtonStyles = {
  borderRadius: 2,
  fontSize: '1rem',
};
const successAlertStyles = { width: '100px' };
const customProgressProps = { lineHeight: 0.5, ml: 2 };
const groupChipStyles = { maxWidth: '100%' };
const createdAtStyles = { color: 'text.secondary' };

const renderGroupTags = (
  selectedGroups: FindGroupOption[],
  getTagProps: AutocompleteRenderGetTagProps,
  formatSummary: (firstGroup: string, additionalCount: number) => string
) => {
  if (!selectedGroups.length) {
    return null;
  }

  const label =
    selectedGroups.length === 1
      ? selectedGroups[0].name
      : formatSummary(selectedGroups[0].name, selectedGroups.length - 1);

  return (
    <Chip
      {...getTagProps({ index: 0 })}
      label={label}
      size="small"
      sx={groupChipStyles}
    />
  );
};

const isValidAmcrId = (value?: string) =>
  !!value && value.split('-').length === 3;

function FoundAtField({ control }: { control: any }) {
  return (
    <Controller
      control={control}
      name="foundAt"
      render={({ field: { ref, onBlur, name, value, onChange } }) => (
        <DateTimeField
          clampLabel
          id="find-date"
          inputRef={ref}
          name={name}
          onBlur={onBlur}
          onChange={onChange}
          required
          value={value}
        />
      )}
    />
  );
}

const FindForm = React.memo(
  ({
    id,
    find,
    eras,
    materials,
    site,
    sites,
    groups,
    tags,
    types,
    initialGroupId,
    isClone = false,
    onSaveDisabledChange,
  }: FindFormProps) => {
    const intl = useIntl();
    const vocabulary = useVocabulary();
    const [images, setImages] = useState([]);
    const navigate = useNavigate();
    const client = useApolloClient();
    const [
      updateFind,
      { called: calledUpdate, loading: loadingUpdate },
    ] = useMutation(UPDATE_FIND_MUTATION, {
      refetchQueries: [GetMyFindQueryName, GetAmcrFindPreviewsQueryName],
      update: (cache, { data }) => {
        const updatedFind = data?.updateFind?.find;

        if (updatedFind) {
          updateFindTagAssignmentInCache(cache, updatedFind);
          reconcileFindsInCache(cache, [updatedFind.id]);
        }
      },
    });
    const [
      createFind,
      { called: calledCreate, loading: loadingCreate },
    ] = useMutation(CREATE_FIND_MUTATION, {
      update: (cache, { data }) => {
        const createdFind = data?.createFind?.find;

        if (createdFind) {
          updateFindTagAssignmentInCache(cache, createdFind);
          reconcileFindsInCache(cache, [createdFind.id]);
        }
      },
    });

    const called = calledUpdate || calledCreate;
    const loading = loadingUpdate || loadingCreate;
    const siteOptions = useMemo<FindSiteOption[]>(
      () => [
        {
          id: null,
          name: intl.formatMessage({
            id: 'general.none',
            defaultMessage: 'None',
          }),
        },
        ...sites,
      ],
      [intl, sites]
    );

    const defaultValues = find
      ? {
          name: find.name,
          evId: find.evId,
          localization: find.localization,
          description: find.description,
          foundAt: DateTime.fromISO(find.foundAt),
          era: find.era,
          type: find.type,
          material: find.material,
          note: find.note,
          latitude: find.latitude,
          longitude: find.longitude,
          siteId: find.siteId,
          amcrId: find.amcrId,
          public: find.visibility === 'public',
          tags: find.tags,
          groupIds: groups.filter((group) =>
            (find.groupIds || []).includes(group.id)
          ),
          imageFiles: undefined,
        }
      : {
          siteId: site,
          groupIds: groups.filter((group) => group.id === initialGroupId),
        };
    const isShared = Boolean(id && find?.shared);

    const {
      reset,
      resetField,
      control,
      handleSubmit,
      getValues,
      setValue,
      formState: { isDirty, isSubmitting },
    } = useForm({
      defaultValues,
    });
    const submitDisabled =
      loading || isSubmitting || (!isDirty && !isClone) || isShared;

    useEffect(() => {
      onSaveDisabledChange?.(submitDisabled);
    }, [onSaveDisabledChange, submitDisabled]);

    const onSubmit = async (data) => {
      let uploadedImages = [];
      const imageFiles = data.imageFiles;
      if (imageFiles && imageFiles.length > 0) {
        const linkQueriesResults = await Promise.all(
          imageFiles.map((file) =>
            client.query({
              query: GET_CLOUDFLARE_DIRECT_LINK_QUERY,
              fetchPolicy: 'no-cache',
              variables: { name: file.name },
            })
          )
        );

        for (const [index, linkQueryResult] of linkQueriesResults.entries()) {
          try {
            const {
              uploadURL,
              id,
            } = linkQueryResult.data.getCloudflareDirectLink;

            if (!uploadURL) {
              throw new Error('Failed to retrieve the direct upload URL.');
            }

            const result = await handleUploadImage(
              imageFiles[index],
              uploadURL,
              id
            );
            uploadedImages.push(result);
          } catch (error) {}
        }
      }

      if (id) {
        await updateFind({
          variables: {
            id,
            find: {
              name: data.name,
              evId: data.evId,
              description: data.description,
              note: data.note,
              type: data.type?.id,
              era: data.era?.id,
              material: data.material,
              foundAt: data.foundAt && data.foundAt.toISO(),
              siteId: data.siteId,
              amcrId: data.amcrId?.trim(),
              localization: data.localization,
              latitude: data.latitude && parseFloat(data.latitude.toString()),
              longitude:
                data.longitude && parseFloat(data.longitude.toString()),
              visibility: data.public ? 'public' : 'private',
              tags: data.tags,
              groupIds: data.groupIds?.length
                ? data.groupIds.map((group) => group.id)
                : null,
              images:
                find?.images || uploadedImages
                  ? [
                      ...(find?.images || []),
                      ...(uploadedImages || []).filter((i) => i),
                    ]
                  : find?.images,
            },
          },
        });
      } else {
        await createFind({
          variables: {
            find: {
              name: data.name,
              evId: data.evId,
              description: data.description,
              note: data.note,
              type: data.type?.id,
              era: data.era?.id,
              material: data.material,
              foundAt: data.foundAt && data.foundAt.toISO(),
              siteId: data.siteId,
              amcrId: data.amcrId?.trim(),
              localization: data.localization,
              latitude: data.latitude && parseFloat(data.latitude.toString()),
              longitude:
                data.longitude && parseFloat(data.longitude.toString()),
              visibility: data.public ? 'public' : 'private',
              tags: data.tags,
              groupIds: data.groupIds?.length
                ? data.groupIds.map((group) => group.id)
                : null,
              images: uploadedImages.length
                ? uploadedImages.filter((image) => image)
                : undefined,
            },
          },
          onCompleted: (createdData) => {
            if (createdData?.createFind?.find) {
              navigate(`/f/${createdData.createFind.find.id}/edit`);
            }
          },
        });
      }

      // add yup validation and error handling and displaying
      reset({ ...data, imageFiles: null });
      setImages([]);
    };

    const pasFiles = find?.amcrData?.files ?? NO_PAS_FILES;

    const handleAmcrFindCreated = useCallback(
      (amcrId: string) => {
        resetField('amcrId', { defaultValue: amcrId });
      },
      [resetField]
    );

    const handleAcceptFiles = (files) => {
      if (files) {
        const value = getValues('imageFiles');
        setValue('imageFiles', [...(value || []), ...files], {
          shouldDirty: true,
        });
      }
    };

    const handleRemoveFile = (file) => {
      const remaining = ((getValues('imageFiles') as File[]) || []).filter(
        (f) => f !== file
      );
      setValue('imageFiles', remaining.length > 0 ? remaining : null, {
        shouldDirty: true,
      });
    };

    const getMaterialLabel = useCallback(
      (material: string) => vocabulary('material', material),
      [vocabulary]
    );

    const handleRenderGroupTags = useCallback(
      (
        selectedGroups: FindGroupOption[],
        getTagProps: AutocompleteRenderGetTagProps
      ) =>
        renderGroupTags(
          selectedGroups,
          getTagProps,
          (firstGroup, additionalCount) =>
            intl.formatMessage(
              {
                id: 'find.field.additionalGroups',
                defaultMessage: '{firstGroup} +{additionalCount} more',
              },
              { firstGroup, additionalCount }
            )
        ),
      [intl]
    );

    return (
      <Stack
        id={FIND_SETTINGS_FORM_ID}
        onSubmit={handleSubmit(onSubmit)}
        component="form"
        sx={containerStyles}
        gap={3}
      >
        <Stack
          direction={{ xs: 'column', sm: 'column', md: 'row' }}
          flexWrap="wrap"
          spacing={2}
          sx={containerStyles}
        >
          <Paper sx={sectionStyles}>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="baseline"
            >
              <Typography variant="h5" gutterBottom>
                <FormattedMessage
                  id="find.section.find"
                  defaultMessage="Find"
                />
              </Typography>
              {!!id && find?.createdAt && (
                <Typography variant="caption" sx={createdAtStyles}>
                  <FormattedMessage
                    id="find.createdAt"
                    defaultMessage="Created {createdAt}"
                    values={{ createdAt: formatDateTime(find.createdAt) }}
                  />
                </Typography>
              )}
            </Stack>
            <Grid container spacing={2}>
              <Grid justifyContent="flex-start" xs={12} md={6}>
                <FormField clampLabel>
                  <FieldLabel htmlFor="find-name">
                    <FormattedMessage
                      id="find.field.name"
                      defaultMessage="Find name"
                    />
                  </FieldLabel>
                  <Controller
                    disabled={isShared}
                    name="name"
                    control={control}
                    render={({ field }) => (
                      <SurfaceTextField
                        id="find-name"
                        type="text"
                        sx={inputStyles}
                        surface="sunken"
                        {...field}
                      />
                    )}
                  />
                </FormField>
              </Grid>
              <Grid justifyContent="flex-start" xs={12} md={6}>
                <FormField clampLabel>
                  <FieldLabel htmlFor="find-evid">EvID</FieldLabel>
                  <Controller
                    disabled={isShared}
                    name="evId"
                    control={control}
                    render={({ field }) => (
                      <SurfaceTextField
                        id="find-evid"
                        type="text"
                        sx={inputStyles}
                        surface="sunken"
                        {...field}
                      />
                    )}
                  />
                </FormField>
              </Grid>
              <Grid justifyContent="flex-start" xs={12}>
                <FormField clampLabel>
                  <FieldLabel htmlFor="find-description">
                    <FormattedMessage
                      id="form.description"
                      defaultMessage="Description"
                    />
                  </FieldLabel>
                  <Controller
                    name="description"
                    disabled={isShared}
                    control={control}
                    render={({ field }) => (
                      <SurfaceTextField
                        multiline
                        minRows={2}
                        maxRows={2}
                        sx={inputStyles}
                        id="find-description"
                        surface="sunken"
                        {...field}
                      />
                    )}
                  />
                </FormField>
              </Grid>
              <Grid justifyContent="flex-start" xs={12} md={6}>
                <FormField clampLabel>
                  <FieldLabel htmlFor="find-type">
                    <FormattedMessage
                      id="find.field.type"
                      defaultMessage="Find type"
                    />
                  </FieldLabel>
                  <Controller
                    name="type"
                    disabled={isShared}
                    control={control}
                    render={({ field: { ref, onChange, ...field } }) => (
                      <SurfaceAutocomplete
                        disablePortal
                        size="small"
                        surface="sunken"
                        id="find-type"
                        options={types}
                        groupBy={(option) =>
                          vocabulary('findType', option.category)
                        }
                        getOptionLabel={(option: {
                          id: string;
                          category: string;
                        }) => vocabulary('findType', option.id)}
                        defaultValue={types.find(
                          (t) => t.id === defaultValues.type
                        )}
                        onChange={(event, newValue) => onChange(newValue)}
                        renderInput={(params) => (
                          <SurfaceTextField
                            sx={inputStyles}
                            inputRef={ref}
                            surface="sunken"
                            {...params}
                            {...field}
                          />
                        )}
                        sx={inputStyles}
                      />
                    )}
                  />
                </FormField>
              </Grid>
              <Grid justifyContent="flex-start" xs={12} md={6}>
                <FormField clampLabel>
                  <FieldLabel htmlFor="find-material">
                    <FormattedMessage
                      id="find.field.material"
                      defaultMessage="Material"
                    />
                  </FieldLabel>
                  <Controller
                    name="material"
                    disabled={isShared}
                    control={control}
                    render={({
                      field: { disabled, name, onBlur, onChange, ref, value },
                    }) => {
                      const handleMaterialChange = (
                        _event: React.SyntheticEvent,
                        newValue: string | null
                      ) => onChange(newValue);

                      return (
                        <MaterialAutocomplete
                          disablePortal
                          disabled={disabled}
                          getOptionLabel={getMaterialLabel}
                          id="find-material"
                          inputName={name}
                          inputRef={ref}
                          onInputBlur={onBlur}
                          options={materials}
                          onChange={handleMaterialChange}
                          sx={inputStyles}
                          value={value || null}
                        />
                      );
                    }}
                  />
                </FormField>
              </Grid>
              <Grid justifyContent="flex-start" xs={12}>
                <FormField clampLabel>
                  <FieldLabel htmlFor="find-era">
                    <FormattedMessage
                      id="find.field.era"
                      defaultMessage="Era"
                    />
                  </FieldLabel>
                  <Controller
                    name="era"
                    disabled={isShared}
                    control={control}
                    render={({ field: { ref, onChange, ...field } }) => (
                      <SurfaceAutocomplete
                        disablePortal
                        size="small"
                        surface="sunken"
                        id="find-era"
                        options={eras}
                        groupBy={(option) => option.parentLabel}
                        getOptionLabel={(option) => option.label}
                        defaultValue={eras.find(
                          (era) =>
                            era.id === defaultValues.era?.toLocaleLowerCase()
                        )}
                        onChange={(event, newValue) => onChange(newValue)}
                        renderInput={(params) => (
                          <SurfaceTextField
                            sx={inputStyles}
                            inputRef={ref}
                            surface="sunken"
                            {...params}
                            {...field}
                          />
                        )}
                        sx={inputStyles}
                      />
                    )}
                  />
                </FormField>
              </Grid>
              <Grid justifyContent="flex-start" xs={12}>
                <FormField clampLabel>
                  <FieldLabel htmlFor="find-note">
                    <FormattedMessage
                      id="find.field.note"
                      defaultMessage="Note"
                    />
                  </FieldLabel>
                  <Controller
                    name="note"
                    disabled={isShared}
                    control={control}
                    render={({ field }) => (
                      <SurfaceTextField
                        multiline
                        minRows={2}
                        maxRows={2}
                        sx={inputStyles}
                        id="find-note"
                        surface="sunken"
                        {...field}
                      />
                    )}
                  />
                </FormField>
              </Grid>
            </Grid>
            {/* Tags are outside of Grid, as they caused growing width bug */}
            <FormField clampLabel my={2}>
              <FieldLabel htmlFor="find-tags">
                <FormattedMessage id="find.field.tags" defaultMessage="Tags" />
              </FieldLabel>
              <Controller
                name="tags"
                disabled={isShared}
                control={control}
                render={({ field: { ref, onChange, ...field } }) => (
                  <SurfaceAutocomplete
                    freeSolo
                    size="small"
                    surface="sunken"
                    multiple
                    id="find-tags"
                    options={tags}
                    defaultValue={tags.filter((t) =>
                      (defaultValues.tags || []).includes(t)
                    )}
                    onChange={(event, newValue) => onChange(newValue)}
                    renderInput={(params) => (
                      <SurfaceTextField
                        inputRef={ref}
                        surface="sunken"
                        {...params}
                        {...field}
                      />
                    )}
                  />
                )}
              />
            </FormField>
            <FormField clampLabel my={2}>
              <FieldLabel htmlFor="find-groups">
                <FormattedMessage
                  id="find.field.groups"
                  defaultMessage="Groups"
                />
              </FieldLabel>
              <Controller
                name="groupIds"
                disabled={isShared}
                control={control}
                render={({ field: { ref, onChange, value, ...field } }) => (
                  <SurfaceAutocomplete
                    {...field}
                    multiple
                    disablePortal
                    size="small"
                    surface="sunken"
                    id="find-groups"
                    options={groups}
                    value={value || []}
                    isOptionEqualToValue={(option, selected) =>
                      option.id === selected.id
                    }
                    getOptionLabel={(option) => option.name}
                    getOptionDisabled={(option) =>
                      Boolean(option.shared && (option.level ?? 0) < 1)
                    }
                    onChange={(event, selected) => onChange(selected)}
                    renderTags={handleRenderGroupTags}
                    renderInput={(params) => (
                      <SurfaceTextField
                        {...params}
                        inputRef={ref}
                        surface="sunken"
                        placeholder={
                          value?.length
                            ? ''
                            : intl.formatMessage({
                                id: 'common.none',
                                defaultMessage: 'None',
                              })
                        }
                      />
                    )}
                  />
                )}
              />
            </FormField>
          </Paper>

          <Paper sx={situationSectionStyles}>
            <Typography variant="h5" gutterBottom>
              <FormattedMessage
                id="find.section.situation"
                defaultMessage="Situation"
              />
            </Typography>
            <Grid container spacing={2}>
              <Grid justifyContent="flex-start" xs={12}>
                <FormField clampLabel>
                  <FieldLabel htmlFor="find-localization">
                    <FormattedMessage
                      id="find.field.localization"
                      defaultMessage="Localization"
                    />
                  </FieldLabel>
                  <Controller
                    disabled={isShared}
                    name="localization"
                    control={control}
                    render={({ field }) => (
                      <SurfaceTextField
                        id="find-localization"
                        type="text"
                        sx={inputStyles}
                        surface="sunken"
                        {...field}
                      />
                    )}
                  />
                </FormField>
              </Grid>
              <Grid justifyContent="flex-start" xs={12} md={6}>
                <FoundAtField control={control} />
              </Grid>
              <Grid justifyContent="flex-start" xs={12} md={6}>
                <FormField clampLabel>
                  <FieldLabel htmlFor="find-site">
                    <FormattedMessage id="form.site" defaultMessage="Site" />
                  </FieldLabel>
                  <Controller
                    name="siteId"
                    control={control}
                    render={({
                      field: { ref, name, onBlur, onChange, value },
                    }) => (
                      <SurfaceAutocomplete<FindSiteOption>
                        disablePortal
                        size="small"
                        surface="sunken"
                        id="find-site"
                        options={siteOptions}
                        disabled={isShared || !!site}
                        getOptionLabel={(option) => option.name}
                        isOptionEqualToValue={(option, selected) =>
                          option.id === selected.id
                        }
                        value={
                          value == null
                            ? siteOptions[0]
                            : siteOptions.find(
                                (option) => option.id === value
                              ) ?? null
                        }
                        onBlur={onBlur}
                        onChange={(event, newValue) =>
                          onChange(newValue?.id ?? null)
                        }
                        renderInput={(params) => (
                          <SurfaceTextField
                            {...params}
                            inputRef={ref}
                            name={name}
                            surface="sunken"
                            sx={inputStyles}
                          />
                        )}
                        sx={inputStyles}
                      />
                    )}
                  />
                </FormField>
              </Grid>

              <Grid justifyContent="flex-start" alignItems="flex-end" xs={12}>
                <FormField clampLabel width="100%">
                  <FieldLabel htmlFor="find-latitude">
                    GPS <RequiredLabel />
                  </FieldLabel>
                  <Stack spacing={2} direction="row" width="100%">
                    <Controller
                      name="latitude"
                      disabled={isShared}
                      control={control}
                      render={({ field }) => (
                        <SurfaceTextField
                          id="find-latitude"
                          type="text"
                          sx={gpsInputStyles}
                          surface="sunken"
                          {...field}
                          onChange={(e) => {
                            const value = e.target.value.replace(',', '.');
                            field.onChange(value);
                          }}
                        />
                      )}
                    />
                    <Controller
                      name="longitude"
                      disabled={isShared}
                      control={control}
                      render={({ field }) => (
                        <SurfaceTextField
                          id="find-longitude"
                          type="text"
                          sx={gpsInputStyles}
                          surface="sunken"
                          {...field}
                          onChange={(e) => {
                            const value = e.target.value.replace(',', '.');
                            field.onChange(value);
                          }}
                        />
                      )}
                    />
                  </Stack>
                </FormField>
              </Grid>
            </Grid>
            <PreviewMapContainer control={control} />
          </Paper>
        </Stack>

        <Stack
          direction={{ xs: 'column', sm: 'column', md: 'row' }}
          flexWrap="wrap"
          spacing={2}
          sx={containerStyles}
        >
          <Paper sx={sectionStyles}>
            <Typography variant="h5" gutterBottom>
              <FormattedMessage
                id="find.menu.settings"
                defaultMessage="Settings"
              />
            </Typography>
            <Grid container spacing={2}>
              <Grid justifyContent="flex-start" xs={12}>
                <FormField clampLabel>
                  <FieldLabel htmlFor="find-amcr-id">AMCR-ID</FieldLabel>
                  <Stack
                    direction="row"
                    alignItems="center"
                    flexWrap="wrap"
                    gap={2}
                  >
                    <Controller
                      disabled={isShared}
                      name="amcrId"
                      control={control}
                      render={({ field }) => {
                        const inputProps = {
                          endAdornment: isValidAmcrId(field.value) ? (
                            <InputAdornment
                              position="end"
                              sx={endAdornmentStyles}
                            >
                              <IconButton
                                LinkComponent={Link}
                                aria-label={intl.formatMessage({
                                  id: 'find.action.openAmcr',
                                  defaultMessage:
                                    'Open find on the AMCR public page',
                                })}
                                edge="end"
                                href={`https://digiarchiv.aiscr.cz/id/${field.value}`}
                                target="_blank"
                              >
                                <OpenInNewIcon fontSize="small" />
                              </IconButton>
                            </InputAdornment>
                          ) : null,
                        };

                        return (
                          <SurfaceTextField
                            id="find-amcr-id"
                            type="text"
                            sx={amcrIdInputStyles}
                            surface="sunken"
                            InputProps={inputProps}
                            {...field}
                          />
                        );
                      }}
                    />
                    {!!id && !isShared && (
                      <AmcrPasControl
                        findId={id}
                        amcrStatus={find?.amcrStatus}
                        pasFiles={pasFiles}
                        control={control}
                        sites={sites}
                        isDirty={isDirty}
                        onCreated={handleAmcrFindCreated}
                      />
                    )}
                  </Stack>
                </FormField>
              </Grid>
              <Grid justifyContent="flex-start" xs={12}>
                <FormField clampLabel>
                  <FieldLabel htmlFor="find-visibility">
                    <FormattedMessage
                      id="find.field.public"
                      defaultMessage="Public"
                    />
                  </FieldLabel>
                  <Controller
                    name="public"
                    control={control}
                    render={({ field: { onChange, value } }) => (
                      <Switch
                        id="find-visibility"
                        disabled={isShared}
                        onChange={(e) => onChange(e.target.checked)}
                        checked={value}
                      />
                    )}
                  />
                </FormField>
              </Grid>
              {!!id && (
                <Grid justifyContent="flex-start" xs={12}>
                  <Stack direction="column">
                    <Typography sx={dangerStyles}>
                      <FormattedMessage
                        id="form.dangerZone"
                        defaultMessage="Danger zone"
                      />
                    </Typography>
                    <DeleteFindControl id={id} siteId={defaultValues.siteId} />
                  </Stack>
                </Grid>
              )}
            </Grid>
          </Paper>
          <Paper sx={sectionStyles}>
            <Typography variant="h5" gutterBottom>
              <FormattedMessage id="find.menu.images" defaultMessage="Images" />
            </Typography>
            <Stack gap={2} sx={imageSectionContentStyles}>
              <ImageDropzone
                files={images}
                setFiles={setImages}
                onFilesAccept={handleAcceptFiles}
                onFileRemove={handleRemoveFile}
              />
              {!!id && (
                <ImageList
                  findId={id}
                  amcrImages={find?.amcrData?.images}
                  images={find.images}
                  mainImage={find.image}
                  readOnly={isShared}
                />
              )}
            </Stack>
          </Paper>
        </Stack>
        <Grid container spacing={2}>
          <Grid justifyContent="flex-start" alignItems="center" xs={8}>
            <Stack direction="row" alignItems="center" gap={1}>
              <Button
                disabled={submitDisabled}
                type="submit"
                variant="contained"
                sx={submitButtonStyles}
              >
                {!isSubmitting ? (
                  <FormattedMessage id="form.submit" defaultMessage="Submit" />
                ) : (
                  <Stack direction="row" alignItems="center">
                    <FormattedMessage
                      id="form.submitting"
                      defaultMessage="Submitting"
                    />{' '}
                    <CustomCircularProgress
                      color="inherit"
                      boxProps={customProgressProps}
                      size={20}
                    />{' '}
                  </Stack>
                )}
              </Button>
              {called && !isDirty && (
                <Alert sx={successAlertStyles} severity="success">
                  <FormattedMessage id="form.saved" defaultMessage="Saved!" />
                </Alert>
              )}
            </Stack>
          </Grid>
        </Grid>
      </Stack>
    );
  }
);

export { FindForm };
